import Swiper from 'swiper';
import { Navigation, Pagination, FreeMode, Thumbs, Autoplay, Zoom } from 'swiper/modules';
import { throttle } from 'throttle-debounce';
import safeJSONParse from '../utils/safeJsonParse';

const MOBILE_BREAKPOINT = 1024;
const thumbSwipers = [];

// Allows for a single swiper instance to be initialised
export const initSwiper = swiper => {
  // Only add the swiper if it doesn't already exist
  if (window.blubolt.swiperObjects.find(sw => sw.el.getAttribute('id') === swiper.getAttribute('id'))) {
    return;
  }

  const slides = swiper.querySelectorAll('.swiper-slide');
  let config = swiper.dataset.swiperConfig ? safeJSONParse(swiper.dataset.swiperConfig, false) : {};
  const mods = [];

  !config ? console.error(`Error in Swiper JSON: ${swiper.dataset.swiperConfig}`) : null;

  if (slides.length && config) {
    // The way Swiper works is that it loads 'modules'
    // Here we're looking for a reference to the module in the config
    // If it's there we add it to the mods array
    if ('navigation' in config && Navigation) {
      mods.push(Navigation);
    }
    if ('pagination' in config && Pagination) {
      mods.push(Pagination);
    }
    if ('freeMode' in config && FreeMode) {
      mods.push(FreeMode);
    }
    if ('autoplay' in config && Autoplay) {
      mods.push(Autoplay);
    }
    if ('thumbs' in config && Thumbs) {
      mods.push(Thumbs);
      thumbSwipers.forEach(thumbSwiper => {
        config = { ...config, ...{ thumbs: { swiper: thumbSwiper } } };
      });
    }
    if ('zoom' in config && Zoom) {
      mods.push(Zoom);
    }

    // We then add the modules to the config
    config = { ...config, ...{ modules: mods } };

    swiper.classList.add('swiper--active');
    const newSwiper = new Swiper(swiper, config);

    // Pagination with loop can be wacky
    // use data-swiper-custom-nav="true"
    // to implement this really basic prev/next logic
    if (swiper.dataset.swiperCustomNav) {
      let currentIndex = 0;
      const prevButton = swiper.querySelector('.swiper-button-prev');
      const nextButton = swiper.querySelector('.swiper-button-next');

      prevButton.addEventListener('click', () => {
        if (currentIndex === 0) {
          currentIndex = newSwiper.slides.length - 1;
        } else {
          currentIndex--;
        }
        newSwiper.slideToLoop(currentIndex);
      });

      nextButton.addEventListener('click', () => {
        if (currentIndex === newSwiper.slides.length - 1) {
          currentIndex = 0;
        } else {
          currentIndex++;
        }
        newSwiper.slideToLoop(currentIndex);
      });
    }

    // If the swiper is for thumbs we push it to the thumbSwipers array
    if (swiper.dataset.swiperThumbs) {
      thumbSwipers.push(newSwiper);
    }

    window.blubolt.swiperObjects.push(newSwiper);
  }
};

export const initMobileSwiper = swiper => {
  // Only add the swiper if it doesn't already exist
  if (window.blubolt.swiperMobileObjects.find(sw => sw.el.getAttribute('id') === swiper.getAttribute('id'))) {
    return;
  }

  const slides = swiper.querySelectorAll('.swiper-slide');
  let config = swiper.dataset.swiperConfig ? safeJSONParse(swiper.dataset.swiperConfig, false) : {};
  const mods = [];
  !config ? console.error('Error in Swiper JSON') : null;
  if (slides.length && config) {
    // The way Swiper works is that it loads 'modules'
    // Here we're looking for a reference to the module in the config
    // If it's there we add it to the mods array
    if ('navigation' in config && Navigation) {
      mods.push(Navigation);
    }
    if ('pagination' in config && Pagination) {
      mods.push(Pagination);
    }
    if ('freeMode' in config && FreeMode) {
      mods.push(FreeMode);
    }
    if ('autoplay' in config && Autoplay) {
      mods.push(Autoplay);
    }
    if ('thumbs' in config && Thumbs) {
      mods.push(Thumbs);
    }
    if ('zoom' in config && Zoom) {
      mods.push(Zoom);
    }

    // We then add the modules to the config
    config = { ...config, ...{ modules: mods } };

    swiper.classList.add('swiper--mob-active');
    const newSwiper = new Swiper(swiper, config);

    // Pagination with loop can be wacky
    // use data-swiper-custom-nav="true"
    // to implement this really basic prev/next logic
    if (swiper.dataset.swiperCustomNav) {
      let currentIndex = 0;
      const prevButton = swiper.querySelector('.swiper-button-prev');
      const nextButton = swiper.querySelector('.swiper-button-next');

      prevButton.addEventListener('click', () => {
        if (currentIndex === 0) {
          currentIndex = newSwiper.slides.length - 1;
        } else {
          currentIndex--;
        }
        newSwiper.slideToLoop(currentIndex);
      });

      nextButton.addEventListener('click', () => {
        if (currentIndex === newSwiper.slides.length - 1) {
          currentIndex = 0;
        } else {
          currentIndex++;
        }
        newSwiper.slideToLoop(currentIndex);
      });
    }

    window.blubolt.swiperMobileObjects.push(newSwiper);
  }
};

/**
 * Standard Swiper instantiation
 */
export const swipify = () => {
  const swipers = document.querySelectorAll('[data-swiper]');

  if (!swipers.length) return;

  swipers.forEach(swiper => {
    initSwiper(swiper);
  });
};

/**
 * Instantiate swipers for mobile only
 */
export const swipifyMob = () => {
  const swipers = document.querySelectorAll('[data-swiper-mob]');

  if (!swipers.length) return;

  const initSwiperOnMobile = () => {
    swipers.forEach(swiper => {
      let mobileBreakpoint = MOBILE_BREAKPOINT;

      if (swiper.dataset.swiperMobBreakpoint) {
        mobileBreakpoint = swiper.dataset.swiperMobBreakpoint;
      }

      if (window.innerWidth < mobileBreakpoint) {
        initMobileSwiper(swiper);
      } else {
        if (window.blubolt.swiperMobileObjects) {
          const keys = Object.keys(window.blubolt.swiperMobileObjects);
          keys.forEach(key => window.blubolt.swiperMobileObjects[key].destroy());
          window.blubolt.swiperMobileObjects = [];
        }
      }
    });
  };

  initSwiperOnMobile();
  window.addEventListener(
    'resize',
    throttle(500, false, () => initSwiperOnMobile())
  );
};

export default swipify;
